/** Hooks de leitura do estado usados pela interface. */
import { useEffect, useState } from 'react';
import { getProductName } from '../config/products';
import { useCatalogStore } from '../goes/catalog';
import { usePlanStore } from '../loop/plan';
import { trechoDoQuadro } from '../loop/semNoaa';
import { usePrevisaoStore } from '../previsao/store';
import { useTimeStore } from '../state/time';
import { useUiStore } from '../state/ui';
import { HOUR_MS } from '../lib/time';
import { MAX_HISTORY_HOURS } from '../loop/frames';
import { useGoesStore } from '../state/goes';

/** "Agora", atualizado a cada `intervalMs` (para textos relativos como "há 5 min"). */
export function useNow(intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

/** Produto da camada base (define a linha do tempo). */
export function useBaseProductId(): string | null {
  return useGoesStore((s) => s.layers[0]?.productId ?? null);
}

/** Instante do quadro mais recente publicado da camada base. */
export function useLatestTime(): number | null {
  const base = useBaseProductId();
  return useCatalogStore((s) => (base ? (s.entries[base]?.listing?.times.at(-1) ?? null) : null));
}

/**
 * Primeiro e último instantes que a interface oferece da camada base: só as
 * últimas 48 h (`MAX_HISTORY_HOURS`), embora a NOAA guarde mais.
 */
export function useAvailableSpan(): [number, number] | null {
  const base = useBaseProductId();
  const first = useCatalogStore((s) => (base ? (s.entries[base]?.listing?.times[0] ?? null) : null));
  const last = useLatestTime();
  return first !== null && last !== null ? [Math.max(first, last - MAX_HISTORY_HOURS * HOUR_MS), last] : null;
}

/**
 * O quadro na tela está num trecho em que a linha do tempo segue sem a base
 * (loop/semNoaa.ts)? Devolve o trecho (desde o último quadro dela; `noFim` =
 * a NOAA ainda não voltou) e o nome do produto; null no normal. Só
 * re-renderiza quando entra ou sai de um trecho.
 */
export function useSemBaseNoQuadro(): { desde: number; ate: number; noFim: boolean; produto: string } | null {
  const trechos = usePlanStore((s) => s.plan?.trechosSemBase);
  const semBaseDesde = usePlanStore((s) => s.plan?.semBaseDesde ?? null);
  const trecho = useTimeStore((s) => trechoDoQuadro(trechos, s.currentTime));
  const base = useBaseProductId();
  if (!trecho || !base) return null;
  return { ...trecho, noFim: trecho.desde === semBaseDesde, produto: getProductName(base) };
}

/**
 * Um painel lateral (desktop) está aberto sobre a borda esquerda do mapa: o das
 * camadas observadas (ui/Sidebar) ou o da previsão (previsao/PainelLateralDaPrevisao).
 * Os chips e legendas que desviam dele usam este, e não só `sidebarOpen`.
 */
export function usePainelLateralAberto(): boolean {
  const observadas = useUiStore((s) => s.sidebarOpen);
  const previsao = usePrevisaoStore((s) => s.modo && s.lateral);
  return observadas || previsao;
}
